import { getPrimaryImage, getShortEquipmentDetails, InstallationData } from "@/installation/installation";
import React, { MutableRefObject, useState } from "react";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRightIcon, ChevronUp } from "lucide-react";
import { capitalizeAndReplace } from "@/lib/utils";
import StaffOnly from "@/user/StaffOnly";
import { Button } from "@/components/ui/button";

const OpportunityEquipmentDetailsCard = (
  {
    installation,
    equipmentDetailsRef,
    defaultExpanded = false,
    collapsible = true,
  }: {
    installation: InstallationData
    equipmentDetailsRef?: MutableRefObject<any>
    defaultExpanded?: boolean
    collapsible?: boolean
  }
) => {

  const [expanded, setExpanded] = useState(defaultExpanded)

  const primaryImage = getPrimaryImage(installation);



  return (
    <Card ref={equipmentDetailsRef} className="p-3 sm:p-4 flex flex-col gap-2">
      <Collapsible open={collapsible ? expanded : true} onOpenChange={collapsible ? setExpanded : undefined}>
        {collapsible && (
          <div className="flex flex-row justify-between items-center">
            <h4 className="font-semibold text-base sm:text-lg">
              Equipment
            </h4>
            <CollapsibleTrigger asChild>
              <button
                title={expanded ? "Show less" : "Show more"}
              className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md flex-shrink-0"
              >
                {
                  expanded ? <ChevronUp size={16} className="text-gray-500"/> :
                    <ChevronDown size={16} className="text-gray-500"/>
                }
              </button>
            </CollapsibleTrigger>
          </div>
        )}
        <div className="flex flex-col gap-2">
          {collapsible ? (
            <div className="text-xs sm:text-sm text-gray-700 break-words">
              {getShortEquipmentDetails(installation.asset)}
              {installation.sublocation && (
                <span className="text-gray-500"> • {installation.sublocation}</span>
              )}
              {installation.serial_number && (
                <span className="text-gray-500"> • SN: {installation.serial_number}</span>
              )}
            </div>
          ) : null}
          <CollapsibleContent>
            <div className="flex flex-col sm:flex-row justify-between text-xs sm:text-sm p-3 bg-gray-50 rounded-lg gap-4">
              <div className="flex flex-col gap-3 flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <p className="text-muted-foreground text-xs">Make</p>
                    <p className="font-medium">{installation.asset.make}</p>
                </div>
                <div>
                    <p className="text-muted-foreground text-xs">Model</p>
                    <p className="font-medium">{installation.asset.model}</p>
                </div>
                <div>
                    <p className="text-muted-foreground text-xs">Type</p>
                    <p className="font-medium">{capitalizeAndReplace(installation.asset.asset_type)}</p>
                </div>
                {
                    installation.asset.nameplate_capacity && (
                    <div>
                        <p className="text-muted-foreground text-xs">Capacity</p>
                        <p className="font-medium">{installation.asset.nameplate_capacity.magnitude} {capitalizeAndReplace(installation.asset.nameplate_capacity.units)}</p>
                    </div>
                  )
                }
                </div>
                <StaffOnly>
                  <div className="pt-3 border-t border-dashed border-gray-300 w-full flex flex-col gap-2">
                    <p className="text-xs text-gray-500 font-medium">Staff only 🕵️‍♂️</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {
                        installation.serial_number && (
                    <div>
                            <p className="text-muted-foreground text-xs">Serial number</p>
                            <p className="font-medium">{installation.serial_number}</p>
                    </div>
                  )
                }
                {
                        installation.asset.asset_subtype && (
                    <div>
                            <p className="text-muted-foreground text-xs">Subtype</p>
                            <p className="font-medium">{capitalizeAndReplace(installation.asset.asset_subtype)}</p>
                    </div>
                  )
                }
                                {
                  installation.expected_voltage_per_phase && (
                    <div>
                            <p className="text-muted-foreground text-xs">Voltage</p>
                            <p className="font-medium">{installation.expected_voltage_per_phase}</p>
                    </div>
                  )
                }
                {
                  (installation.equipment_model?.refrigerant_type || installation.asset.refrigerant_type) && (
                    <div>
                            <p className="text-muted-foreground text-xs">Refrigerant Type</p>
                            <p className="font-medium">{installation.equipment_model?.refrigerant_type || installation.asset.refrigerant_type}</p>
                    </div>
                  )
                }
                    <div>
                        <p className="text-muted-foreground text-xs">Power Configuration</p>
                        <p className="font-medium">{installation.phase_system_type || 'N/A'}</p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-xs">Sensor kit serial number</p>
                        <p className="font-medium">{installation.sensor_kit}</p>
                      </div>
                    </div>
                  </div>
                </StaffOnly>

                {
                  installation.asset_manual && (
                    <div>
                      <a
                        href={installation.asset_manual || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-fit block mt-2"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center bg-white"
                        >
                          Asset manual
                          <ChevronRightIcon className="ml-2"/>
                        </Button>
                      </a>
                    </div>
                  )
                }
              </div>
              {primaryImage && (
                <img
                  src={primaryImage.image_url}
                  alt="Asset Image"
                  className="w-full sm:max-w-[40%] max-h-64 object-cover rounded-lg self-center sm:self-start"
                />
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </Card>
  );
}

export default OpportunityEquipmentDetailsCard;
